/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';

interface InsufficientBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  requiredAmount?: number;
  currentBalance?: number;
}

const InsufficientBalanceModal: React.FC<InsufficientBalanceModalProps> = ({
  isOpen,
  onClose,
  message,
  requiredAmount,
  currentBalance,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleGoToWallet = () => {
    onClose();
    navigate('/billing/payments?action=topup');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-400"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-amber-400" />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-semibold text-white text-center mb-2">
            Insufficient Balance
          </h3>

          {/* Message */}
          <p className="text-gray-400 text-center mb-4">
            {message || 'Your wallet balance is too low to create this resource. Please top up your wallet to continue.'}
          </p>

          {/* Balance info */}
          {(requiredAmount !== undefined || currentBalance !== undefined) && (
            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              {currentBalance !== undefined && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Current Balance</span>
                  <span className="font-medium text-white">₹{currentBalance.toFixed(2)}</span>
                </div>
              )}
              {requiredAmount !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Minimum Required</span>
                  <span className="font-medium text-amber-400">₹{requiredAmount.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-600 text-gray-300 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleGoToWallet}
              className="flex-1 px-4 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
            >
              <WalletIcon className="w-5 h-5" />
              Top Up Wallet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsufficientBalanceModal;
